// ─────────────────────────────────────────────────────────────────────────────
// MeineAufgaben – eigene Seite mit allen mir zugewiesenen offenen Maßnahmen
//
// Zeigt nach Status sortiert (zuerst "in Arbeit", dann "offen"), klick auf
// Eintrag öffnet das Baumdetail. Direkt-Aktionen: "In Arbeit nehmen",
// "Als erledigt markieren".
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, Button, Skeleton, alpha,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete,
  CircularProgress, ListSubheader, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Assignment, Park, Warning, CheckCircle, PlayArrow, Add, Forest, Replay } from '@mui/icons-material';
import api, { measuresAPI } from '../services/api';
import EmptyState from '../components/EmptyState';
import { MEASURE_TYPE_OPTIONS, MEASURE_TYPE_GROUPS, getMeasureBeschreibung } from '../data/treeFieldOptions';
import { useAuth } from '../contexts/AuthContext';

export default function MeineAufgaben() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const isDark = theme.palette.mode === 'dark';
  const [tasks, setTasks] = useState<any[]>([]);
  const [view, setView] = useState<'offen' | 'erledigt'>('offen');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState('');

  // Neue Aufgabe – Dialog State (BUG-017 / FEAT-024)
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    measure_type: string;
    description: string;
    planned_date: string;
    tree: any | null;     // gewähltes Baum-Objekt (oder null = freie Aufgabe)
    flaeche: any | null;  // gewählte Waldfläche (alternativ zum Baum)
    assigned_to: string;  // '' = mir selbst
  }>({ measure_type: '', description: '', planned_date: '', tree: null, flaeche: null, assigned_to: '' });

  // Zuweisen an andere Nutzer dürfen nur Sachbearbeiter (admin + erfasser).
  const canAssign = user?.role === 'admin' || user?.role === 'erfasser';
  const [selectableUsers, setSelectableUsers] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!canAssign) return;
    api.get('/users/selectable').then(r => setSelectableUsers(r.data)).catch(() => setSelectableUsers([]));
  }, [canAssign]);

  // Waldflächen-Liste für den Bezug-Picker
  const [flaechenOptions, setFlaechenOptions] = useState<any[]>([]);
  useEffect(() => {
    if (!createOpen || flaechenOptions.length > 0) return;
    api.get('/flaechen').then(res => setFlaechenOptions(res.data || [])).catch(() => {});
  }, [createOpen, flaechenOptions.length]);

  // Baum-Suche im Dialog: Autocomplete sucht serverseitig
  const [treeQuery, setTreeQuery] = useState('');
  const [treeOptions, setTreeOptions] = useState<any[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);

  // Debounced Baum-Suche: nach 250ms Tippstopp nach Baumnummer suchen
  useEffect(() => {
    if (!createOpen) return;
    if (treeQuery.trim().length < 1) { setTreeOptions([]); return; }
    setTreeLoading(true);
    const handle = setTimeout(() => {
      api.get('/trees', { params: { search: treeQuery, limit: 15 } })
        .then(res => {
          // Backend antwortet mit { data: [...], pagination: {...} }
          const items = Array.isArray(res.data)
            ? res.data
            : (res.data.data || res.data.items || res.data.trees || []);
          setTreeOptions(items);
        })
        .catch(() => setTreeOptions([]))
        .finally(() => setTreeLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [treeQuery, createOpen]);

  const handleCreate = async () => {
    if (!form.measure_type) return;
    setSaving(true);
    try {
      await api.post('/measures', {
        tree_id: form.tree?.id || null,       // null = freie Aufgabe
        flaeche_id: form.flaeche?.id || null, // alternativer Bezug: Waldfläche
        measure_type: form.measure_type,
        description: form.description || null,
        planned_date: form.planned_date || null,
        status: 'offen',
        // Sachbearbeiter dürfen an andere zuweisen; sonst (bzw. leer) sich selbst.
        assigned_to: (canAssign && form.assigned_to) ? form.assigned_to : (user?.id || null),
      });
      setSnackbar('Aufgabe wurde angelegt');
      setCreateOpen(false);
      setForm({ measure_type: '', description: '', planned_date: '', tree: null, flaeche: null, assigned_to: '' });
      setTreeQuery('');
      load();
    } catch (err: any) {
      setSnackbar('Fehler: ' + (err.response?.data?.error || 'Konnte nicht angelegt werden'));
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get('/measures/my-tasks', { params: view === 'erledigt' ? { status: 'erledigt' } : {} })
      .then(res => setTasks(res.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (mid: string, newStatus: string) => {
    try {
      const completed_date = newStatus === 'erledigt' ? new Date().toISOString().split('T')[0] : undefined;
      await measuresAPI.update(mid, { status: newStatus, completed_date });
      setSnackbar(newStatus === 'erledigt' ? 'Maßnahme als erledigt markiert' : 'Status aktualisiert');
      load();
    } catch {
      setSnackbar('Fehler beim Aktualisieren');
    }
  };

  const inArbeit = tasks.filter((t: any) => t.status === 'in Arbeit');
  const offen    = tasks.filter((t: any) => t.status === 'offen');

  const renderTask = (m: any) => {
    const inProgress = m.status === 'in Arbeit';
    const isDone = m.status === 'erledigt' || m.status === 'abgebrochen';
    return (
      <Card key={m.id} sx={{ mb: 1, border: '1px solid', borderColor: inProgress ? alpha(theme.palette.warning.main, 0.5) : 'divider', bgcolor: inProgress ? alpha(theme.palette.warning.main, isDark ? 0.10 : 0.06) : 'background.paper' }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {/* Icon: Fläche → Forest, sonst Baum */}
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, bgcolor: alpha(theme.palette.success.main, 0.15), color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {m.flaeche_id ? <Forest sx={{ fontSize: 20 }} /> : <Park sx={{ fontSize: 20 }} />}
            </Box>
            <Box
              sx={{ flex: 1, minWidth: 0, cursor: (m.tree_id || m.flaeche_id) ? 'pointer' : 'default' }}
              onClick={() => { if (m.tree_id) navigate(`/trees/${m.tree_id}`); else if (m.flaeche_id) navigate(`/flaechen/${m.flaeche_id}`); }}
            >
              {/* Status + Maßnahmentyp */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap', mb: 0.5 }}>
                {m.status === 'erledigt' ? (
                  <Chip label="✓ Erledigt" size="small" color="success" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }} />
                ) : m.status === 'abgebrochen' ? (
                  <Chip label="Abgebrochen" size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }} />
                ) : inProgress ? (
                  <Chip label="⟳ In Arbeit" size="small" color="warning" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }} />
                ) : (
                  <Chip label="Offen" size="small" color="info" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }} />
                )}
                <Typography variant="body2" fontWeight={700}>{m.measure_type}</Typography>
              </Box>
              {/* Bezug: Baum, Fläche oder freie Aufgabe */}
              {m.tree_id && m.baumnummer ? (
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  <strong>Baum #{m.baumnummer}</strong>{m.baumart ? ` · ${m.baumart}` : ''}{m.ort ? ` · ${m.ort}` : ''}{m.strasse ? `, ${m.strasse}` : ''}
                </Typography>
              ) : m.flaeche_id ? (
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  <strong>Fläche: {m.flaeche_name || 'Waldfläche'}</strong>
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontStyle: 'italic', color: 'text.secondary' }}>
                  Freie Aufgabe (kein Baum)
                </Typography>
              )}
              {m.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontSize: '0.75rem' }}>
                  {m.description}
                </Typography>
              )}
              {/* Schaden-Verknüpfung */}
              {m.damage_id && m.linked_damage_type && (
                <Chip
                  label={`Aus Schaden: ${m.linked_damage_type}`}
                  size="small"
                  icon={<Warning sx={{ fontSize: 14 }} />}
                  sx={{ mt: 0.7, height: 20, fontSize: '0.68rem', bgcolor: alpha(theme.palette.warning.main, 0.15), color: 'warning.main' }}
                />
              )}
              {/* Meta-Zeile */}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                {m.planned_date && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Geplant: {new Date(m.planned_date).toLocaleDateString('de-DE')}
                  </Typography>
                )}
                {m.assigned_by_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Zugewiesen von: {m.assigned_by_name}
                  </Typography>
                )}
                {isDone && m.completed_date && (
                  <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                    Erledigt: {new Date(m.completed_date).toLocaleDateString('de-DE')}
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Aktionen */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
              {isDone ? (
                <Button size="small" variant="outlined" startIcon={<Replay sx={{ fontSize: 16 }} />} onClick={() => handleStatus(m.id, 'offen')} sx={{ fontSize: '0.72rem', minHeight: 28 }}>
                  Wieder öffnen
                </Button>
              ) : (
                <>
                  {!inProgress && (
                    <Button size="small" variant="outlined" color="warning" startIcon={<PlayArrow sx={{ fontSize: 16 }} />} onClick={() => handleStatus(m.id, 'in Arbeit')} sx={{ fontSize: '0.72rem', minHeight: 28 }}>
                      Annehmen
                    </Button>
                  )}
                  <Button size="small" variant="contained" color="success" startIcon={<CheckCircle sx={{ fontSize: 16 }} />} onClick={() => handleStatus(m.id, 'erledigt')} sx={{ fontSize: '0.72rem', minHeight: 28 }}>
                    Erledigt
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Assignment sx={{ color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Meine Aufgaben</Typography>
        {canAssign && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={(e) => { e.currentTarget.blur(); setCreateOpen(true); }}
          sx={{ bgcolor: '#4a7c59', '&:hover': { bgcolor: '#3a6447' } }}
        >
          Neue Aufgabe
        </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {view === 'erledigt'
          ? 'Deine bereits erledigten (oder abgebrochenen) Maßnahmen'
          : 'Alle Maßnahmen, die dir zugewiesen sind und noch offen sind'}
      </Typography>

      <ToggleButtonGroup value={view} exclusive size="small" sx={{ mb: 2.5 }}
        onChange={(_, v) => { if (v) setView(v); }}>
        <ToggleButton value="offen" sx={{ textTransform: 'none', px: 2 }}>Offen</ToggleButton>
        <ToggleButton value="erledigt" sx={{ textTransform: 'none', px: 2 }}>Erledigt</ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={100} sx={{ borderRadius: 2, mb: 1 }} />)
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={CheckCircle}
          title={view === 'erledigt' ? 'Keine erledigten Aufgaben' : 'Keine offenen Aufgaben'}
          description={view === 'erledigt'
            ? 'Hier erscheinen deine erledigten oder abgebrochenen Maßnahmen.'
            : 'Aktuell ist dir keine offene Maßnahme zugewiesen. Wenn dir jemand eine zuweist oder du selbst eine anlegst, erscheint sie hier.'}
          accentColor="success"
        />
      ) : view === 'erledigt' ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.7 }}>
            Erledigt <Chip label={tasks.length} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
          </Typography>
          {tasks.map(renderTask)}
        </>
      ) : (
        <>
          {inArbeit.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                ⟳ Aktuell in Arbeit <Chip label={inArbeit.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Typography>
              {inArbeit.map(renderTask)}
              <Box sx={{ mb: 2.5 }} />
            </>
          )}
          {offen.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'info.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                Offen <Chip label={offen.length} size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Typography>
              {offen.map(renderTask)}
            </>
          )}
        </>
      )}

      {/* Dialog: Neue Aufgabe anlegen (BUG-017 / FEAT-024) */}
      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#4a7c59', color: 'white', py: 1.5 }}>
          Neue Aufgabe anlegen
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Du kannst die Aufgabe an einen Baum binden oder eine freie Aufgabe anlegen (ohne Baumzuordnung).
          </Typography>

          {/* Maßnahmentyp (Pflicht) */}
          <FormControl fullWidth size="small">
            <InputLabel>Maßnahmentyp *</InputLabel>
            <Select
              value={form.measure_type}
              label="Maßnahmentyp *"
              onChange={e => {
                const newType = e.target.value;
                // Auto-Fill: Beim Wechsel wird die FLL-Beschreibung in die Beschreibung
                // übernommen – aber nur wenn die Beschreibung leer ist oder noch
                // die Beschreibung des vorherigen Typs drin steht (BUG-021)
                const prevBeschr = getMeasureBeschreibung(form.measure_type);
                const newBeschr  = getMeasureBeschreibung(newType);
                const cur = form.description || '';
                const shouldFill =
                  cur.trim() === '' ||
                  cur.trim() === prevBeschr.trim();
                setForm({
                  ...form,
                  measure_type: newType,
                  description: shouldFill ? newBeschr : cur,
                });
              }}
            >
              {MEASURE_TYPE_GROUPS.flatMap((group) => [
                <ListSubheader key={`hdr-${group.label}`} sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', lineHeight: '28px', bgcolor: 'background.paper' }}>
                  {group.label}
                </ListSubheader>,
                ...group.items.map(t => (
                  <MenuItem key={t.value} value={t.value} sx={{ pl: 3, py: 0.7 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.value}</Typography>
                      {t.beschreibung !== t.value && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t.beschreibung}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          {/* Bezug (optional): Baum ODER Waldfläche. Beides leer = freie Aufgabe. */}
          <Autocomplete
            size="small"
            value={form.tree}
            onChange={(_, v) => setForm({ ...form, tree: v, flaeche: v ? null : form.flaeche })}
            onInputChange={(_, v) => setTreeQuery(v)}
            options={treeOptions}
            loading={treeLoading}
            getOptionLabel={(o) => o?.baumnummer ? `#${o.baumnummer}${o.baumart ? ' · ' + o.baumart : ''}${o.ort ? ' · ' + o.ort : ''}` : ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText={treeQuery ? 'Keine Bäume gefunden' : 'Baumnummer eingeben…'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Baum (optional)"
                placeholder="Baumnummer eingeben, z.B. AT328"
                helperText="Leer lassen für eine freie Aufgabe ohne Baumbezug"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {treeLoading && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Waldfläche (optional, alternativ zum Baum) */}
          <Autocomplete
            size="small"
            value={form.flaeche}
            onChange={(_, v) => setForm({ ...form, flaeche: v, tree: v ? null : form.tree })}
            options={flaechenOptions}
            getOptionLabel={(o) => o?.name ? `${o.name}${o.typ ? ' · ' + o.typ : ''}${o.ort ? ' · ' + o.ort : ''}` : ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText="Keine Flächen vorhanden"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Waldfläche (optional)"
                placeholder="Fläche auswählen"
                helperText="Alternativ zum Baum – für eine Maßnahme an einer Fläche"
              />
            )}
          />

          {/* Beschreibung */}
          <TextField
            fullWidth
            size="small"
            label="Beschreibung"
            multiline
            rows={2}
            placeholder="Was ist zu tun?"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
          />

          {/* Geplantes Datum */}
          <TextField
            fullWidth
            size="small"
            label="Geplantes Datum"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.planned_date}
            onChange={e => setForm({ ...form, planned_date: e.target.value })}
          />

          {/* Zuweisen an – nur für Sachbearbeiter (admin + erfasser) */}
          {canAssign && (
            <FormControl fullWidth size="small">
              <InputLabel shrink>Zuweisen an</InputLabel>
              <Select label="Zuweisen an" notched displayEmpty value={form.assigned_to}
                onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                <MenuItem value=""><em>Mir selbst</em></MenuItem>
                {selectableUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !form.measure_type}
            sx={{ bgcolor: '#4a7c59' }}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Add />}
          >
            Aufgabe anlegen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3500} onClose={() => setSnackbar('')}
        message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
